import React, { useState } from 'react';

import { Autocomplete, Button, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { useGetReportingLensFields } from '~/hooks/apis/reporting/useLensAuthoring';

import type { Filter, LensDocumentData } from './types';
import type { BoardEditor } from './useBoardEdit';

const AUTO = 'auto';

const STYLES: Array<[string, string]> = [
  [AUTO, 'Auto'],
  ['chip', 'Chip'],
  ['segmented', 'Buttons'],
  ['select', 'List'],
  ['range', 'Range'],
];

const bound = (text: string) => (text.trim() === '' || !Number.isFinite(Number(text)) ? undefined : Number(text));

type FilterFormProps = { data?: LensDocumentData; editor: BoardEditor; filter?: Filter; taken: Array<string>; blockCount: number; onDone: () => void };

export default function FilterForm({ data, editor, filter, taken, blockCount, onDone }: FilterFormProps) {
  const fields = useGetReportingLensFields(data?.lensId, data?.lensVersion);
  const [column, setColumn] = useState(filter?.column ?? '');
  const [label, setLabel] = useState(filter?.label ?? '');
  const [style, setStyle] = useState<string>(filter?.style ?? AUTO);
  const [value, setValue] = useState<Array<string>>(filter?.value ?? []);
  const [single, setSingle] = useState(filter?.multiple === false);
  const [min, setMin] = useState(filter?.range?.min === undefined ? '' : String(filter.range.min));
  const [max, setMax] = useState(filter?.range?.max === undefined ? '' : String(filter.range.max));
  const all = [
    ...new Map(
      (fields.data?.datasets ?? []).flatMap((dataset) => dataset.columns.filter((entry) => entry.kind !== 'date' && !taken.includes(entry.name)).map((entry) => [entry.name, entry] as const)),
    ).values(),
  ];
  const columns = all.map((entry): [string, string] => [entry.name, entry.label]);
  const numeric = (name: string) => all.find((entry) => entry.name === name)?.kind === 'measure';
  const range = { ...(bound(min) !== undefined ? { min: bound(min) } : {}), ...(bound(max) !== undefined ? { max: bound(max) } : {}) };
  const reach = (name: string) => fields.data?.reach[name]?.length ?? 0;
  const save = () => {
    editor.apply({
      op: 'filter',
      column,
      def:
        style === 'range'
          ? { style, label: label.trim() || null, value: null, multiple: null, range: Object.keys(range).length ? range : null }
          : { style: style === AUTO ? null : style, label: label.trim() || null, value: value.length ? value : null, multiple: single ? false : null, range: null },
    });
    onDone();
  };

  return (
    <Stack data-lens-filter-form spacing={1.5} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
      {filter ? null : (
        <TextField
          disabled={fields.isLoading}
          fullWidth
          helperText={fields.error ? 'The columns couldn’t be read.' : column ? `Reaches ${reach(column)} of ${blockCount} blocks` : undefined}
          label="Column"
          onChange={(event) => {
            setColumn(event.target.value);
            setValue([]);
            setStyle(numeric(event.target.value) ? 'range' : AUTO);
          }}
          select
          size="small"
          value={column}
        >
          {columns.map(([name, text]) => (
            <MenuItem key={name} value={name}>
              <Stack>
                <Typography variant="body2">{text}</Typography>
                <Typography color="text.secondary" variant="caption">{`Reaches ${reach(name)} of ${blockCount} blocks`}</Typography>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField fullWidth label="Label" onChange={(event) => setLabel(event.target.value)} placeholder={columns.find(([name]) => name === column)?.[1]} size="small" value={label} InputLabelProps={{ shrink: true }} />
      <ToggleButtonGroup
        aria-label="Style"
        exclusive
        fullWidth
        onChange={(_, next: string | null) => next && setStyle(next)}
        size="small"
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
        value={style}
      >
        {STYLES.map(([option, text]) => (
          <ToggleButton key={option} value={option}>
            {text}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {style === 'range' ? (
        <Stack direction="row" spacing={1}>
          <TextField InputLabelProps={{ shrink: true }} label="Opens from" onChange={(event) => setMin(event.target.value)} placeholder="Any" size="small" value={min} />
          <TextField InputLabelProps={{ shrink: true }} label="Opens to" onChange={(event) => setMax(event.target.value)} placeholder="Any" size="small" value={max} />
        </Stack>
      ) : (
        <ToggleButtonGroup aria-label="Readers pick" exclusive fullWidth onChange={(_, next: string | null) => next && setSingle(next === 'one')} size="small" sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }} value={single ? 'one' : 'many'}>
          <ToggleButton value="many">Any number of values</ToggleButton>
          <ToggleButton value="one">One at a time</ToggleButton>
        </ToggleButtonGroup>
      )}
      {style === 'range' ? null : (
        <Autocomplete
          freeSolo
          multiple
          onChange={(_, next) => setValue(next.map(String))}
          options={column ? data?.options?.[column] ?? [] : []}
          renderInput={(params) => <TextField {...params} helperText="Empty shows everything" label="Opens on" size="small" />}
          size="small"
          value={value}
        />
      )}
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={onDone} size="small" variant="text">
          Cancel
        </Button>
        <Button disabled={!column || editor.pending} onClick={save} size="small" variant="contained">
          {filter ? 'Save filter' : 'Add filter'}
        </Button>
      </Stack>
    </Stack>
  );
}
